import { Box, Button, CircularProgress } from '@mui/material';
import { GridColDef, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import DataGrid from 'components/data-grid';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import LoadingSpinner from 'components/loading-spinner';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs, { Dayjs } from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { isValidDecimal, isValidMeanWeight } from 'utils/validate-numbers';
import { getSampling, putSampling } from 'views/production-systems/api/productionSystems';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { UpdateSamplingInput } from 'views/production-systems/features/registration-log/types/update-sampling-input.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { calculateBiomass, calculateMeanWeight } from 'views/production-systems/utils/farm-overview';
import RegistrationHeader from '../../../components/registration-header';

type EditSampleProps = {
  logId: number;
  eventNr: number;
  handleClose?: () => void;
  canEdit: boolean;
  dateCreated: Dayjs;
};

type ParamTypes = {
  globalSiteId: string;
};

type Row = {
  pondId: string;
  pondName: string;
  cycleId: number;
  zoneName: string;
  cycleName: string;
  currentMw: number;
  currentQty: number;
  currentBm: number;
  newMw: number;
  survivalRate: number;
};

const COSTAMARKET_ID = 'b3f2a71f-bbf5-42fa-b040-918bd43a9373';

const PAGE_NAME_ID = 'productionSystems.farmOverview.sampling';

const EditSampling: React.FC<EditSampleProps> = ({ handleClose, logId, eventNr, canEdit = true, dateCreated }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const { lockedPonds, globalSiteId, lockPonds, releasePonds } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();
  const {
    account: { company },
  } = useAccountState();

  const [confirm, setConfirm] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(!canEdit);
  const [rows, setRows] = useState<readonly Row[]>([]);
  const survivalRatePercentage = 150;

  //#region FETCH DATA

  const { data: sampling, isFetching } = useGet(
    ['getSampling', globalSiteId, logId, eventNr],
    ({ queryKey }) => getSampling(String(queryKey[1]), Number(queryKey[2]), Number(queryKey[3]))
  );

  useEffect(() => {
    if (sampling && canEdit) lockPonds(sampling.cycles.map((cycle) => cycle.pondId));
  }, [canEdit, lockPonds, sampling]);

  //#endregion FETCH DATA

  //#region DATA GRID

  useEffect(() => {
    if (!!sampling) {
      let rows: readonly any[] = sampling.cycles.map((cycle) => {
        return {
          pondName: cycle.pondName,
          pondId: cycle.pondId,
          cycleName: cycle.cycleName,
          cycleId: cycle.cycleId,
          zoneName: cycle.zoneName,
          currentMw: cycle.previousMw,
          currentQty: cycle.previousQty,
          currentBm: calculateBiomass(cycle.previousMw, cycle.previousQty),
          newMw: cycle.weight,
          survivalRate: cycle.survivalRate,
        };
      });

      setRows(rows);
    }
  }, [sampling]);

  const processRowUpdate = (newRow: GridRowModel<Row>) => {
    const updatedRow = { ...newRow };
    setRows(rows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
    return updatedRow;
  };

  const disableSubmit = useMemo(() => {
    return rows.some((row) => {
      const survivalRate = Number(row.survivalRate);

      const invalidNewMw = !isValidDecimal(row.newMw);
      const invalidSurvivalRate = !isValidDecimal(row.survivalRate) || survivalRate < 1 || survivalRate > survivalRatePercentage;

      // If company is COSTAMARKET, ignore survivalRate validation
      return company!.companyId === COSTAMARKET_ID ? invalidNewMw : invalidNewMw || invalidSurvivalRate;
    });
  }, [rows, company, survivalRatePercentage]);

  const summaryValues: SummaryItem[] = useMemo(() => {
    let newSummary = rows.reduce(
      (prev, curr, idx) => {
        let biomass = calculateBiomass(curr.currentMw, curr.currentQty);

        return {
          survivalRate: prev.survivalRate + curr.currentQty,
          currentQty: prev.currentQty + curr.currentQty,
          currentBm: prev.currentBm + biomass,
          currentMw: 0,
          newMw: 0,
        };
      },
      {
        survivalRate: 0,
        currentQty: 0,
        currentMw: 0,
        currentBm: 0,
        newMw: 0,
      }
    );

    newSummary.currentMw = calculateMeanWeight(newSummary.currentBm, newSummary.currentQty);
    newSummary.newMw = newSummary.newMw / rows.length;

    return [
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.currentQty,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.currentMw,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.currentBm,
        type: 'number',
        hidden: false,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      ...(company!.companyId !== COSTAMARKET_ID
        ? [
          {
            value: undefined,
            type: undefined,
            hidden: true,
          },
        ]
        : []),
    ];
  }, [rows]);

  /**
   * This column should be hidden if the company is COSTAMARKET, a shrimp customer in Production, as they do not use survival rate in bluViu
   */
  const survivalRateColumn: GridColDef = useMemo(() => {
    return {
      field: 'survivalRate',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.survivalRate` }),
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      editable: !readOnly,
      type: 'number',
      valueFormatter: (value: number) => `${value}%`,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError =
          !isValidDecimal(params.props.value) || Number(params.props.value) < 1 || Number(params.props.value) > survivalRatePercentage;

        changeErrorDictionary({ ...errorDictionary, [`${params.id}-survivalRate`]: hasError });
        return { ...params.props, error: hasError };
      },
    };
  }, [company]);

  const columns: GridColDef[] = useMemo(
    () =>
      [
        {
          field: 'pondName',
          flex: 2,
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.destinationPond` }),
          editable: false,
          renderCell: ({ row }) => {
            return (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  width: '100%',
                }}>
                {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
                <Box
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={row.pondName}>
                  {row.pondName}
                </Box>
              </Box>
            );
          },
        },
        {
          field: 'cycleName',
          flex: 1,
          editable: false,
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.cycleName` }),
          align: 'left',
          headerAlign: 'left',
        },
        {
          field: 'zoneName',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.zone` }),
          type: 'string',
          editable: false,
          align: 'left',
          headerAlign: 'left',
          flex: 1,
        },
        {
          field: 'currentQty',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.number` }),
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          editable: false,
          flex: 1,
        },
        {
          field: 'currentMw',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.meanWeight` }),
          sortable: false,
          editable: false,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          flex: 1,
        },
        {
          field: 'currentBm',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.biomass` }),
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          editable: false,
          flex: 1,
        },
        {
          field: 'newMw',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.newMeanWeight` }),
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          editable: !readOnly,
          flex: 1,
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const value = Number(params.props.value);

            const hasError = !isValidDecimal(params.props.value) || !isValidMeanWeight(value);
            changeErrorDictionary({ ...errorDictionary, [`${params.id}-newMw`]: hasError });
            return { ...params.props, error: hasError };
          },
        },
        ...(company!.companyId !== COSTAMARKET_ID ? [survivalRateColumn] : []), // Hide for COSTAMARKET customer
      ] as GridColDef[],
    [readOnly]
  );

  //#endregion DATA GRID

  //#region HANDLERS
  const handleSubmit = () => {
    let d: UpdateSamplingInput = {
      cycles: rows.map((row) => ({
        cycleId: row.cycleId,
        survivalRate: company!.companyId !== COSTAMARKET_ID ? row.survivalRate : 100, // COSTAMARKET should always be 100% survival rate
        weight: row.newMw,
        pondId: row.pondId,
      })),
    };

    updateSampling.mutate({ globalSiteId, data: d });
  };
  //#endregion HANDLERS

  //#region UPDATE DATA

  const updateSampling = usePost<AxiosResponse, { globalSiteId: string; data: UpdateSamplingInput }>(
    (request) => putSampling(params.globalSiteId, logId, request.data),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      sampling && releasePonds(sampling.cycles.map((cycle) => cycle.pondId));
      queryClient.invalidateQueries({
        queryKey: ['getRegistrations']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion UPDATE DATA

  //#region HELPERS

  useEffect(() => {
    if (canEdit) {
      if (lockedPonds.notGranted.length > 0 && confirm) {
        setReadOnly(true);
      } else {
        setReadOnly(false);
      }
    }
  }, [confirm, canEdit, lockedPonds.notGranted]);

  //#endregion HELPERS

  return isFetching ? (
    <LoadingSpinner />
  ) : lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} fromAdd={false} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      {!readOnly && <PondLockTimer handleDialogClose={handleClose} />}
      <Box display={'flex'} flexDirection={'column'} gap={8}>
        <RegistrationHeader dateCreated={dateCreated} />
        <DataGrid
          rows={rows}
          apiRef={apiRef}
          columns={columns}
          getRowId={(row) => row.pondId}
          onCellEditStop={handleEditing}
          processRowUpdate={processRowUpdate}
          showToolbar
          slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: `${intl.formatMessage({
                  id: 'productionSystems.registrationLog.registrationTypes.meanweightSample',
                })}-${dayjs().format('DD/MM/YYYY')}`,
              },
            },
            footer: { summaryValues: summaryValues } as DataGridSummaryProps,
          }}
          onRowClick={({ id }) => {
            const { field } = apiRef.current?.state.tabIndex.cell ?? {};
            apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
          }}
        />
      </Box>
      {!readOnly && (
        <Box display={'flex'} justifyContent={'flex-end'}>
          <Button
            disabled={updateSampling.isPending || disableSubmit || isEditing || hasError}
            onClick={handleSubmit}
            startIcon={updateSampling.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
            <FormattedMessage id={'general.confirm'} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(EditSampling);
